import { getPlatform } from '../functions/getPlatform';
import { IStorageItem } from '../interfaces/Storage';
import { SecretService } from './SecretService';

/**
 * Used only for session persistence for now.
 *
 * @class StorageService
 *
 * @internal
 */
class StorageService {
	private _mockStorage: any = {};
	private _storage: Storage;

	/**
	 * @constructor for StorageService
	 * Determines whether sessionStorage is usable or not
	 */
	constructor() {
		if (getPlatform() === 'BROWSER') {
			this._storage = sessionStorage;
		} else {
			this._storage = this.setMockStorage();
		}
	}

	/**
	 * Sets the desired object in the storage (expiration in hours)
	 * @param key
	 * @param value
	 * @param expiration
	 */
	public set = (key: string, value: any, expiration: number): void => {
		const data = SecretService.encrypt({ key, value, expiration, timestamp: Date.now() });

		this._storage.setItem(key, data);
	}

	/**
	 * Gets a desired object from the storage
	 * @param key
	 */
	public get = (key: string): string | undefined => {

		if (this._storage.getItem(key)) {
			try {
				const data = this._storage.getItem(key);
				const resolved = SecretService.decrypt<IStorageItem>(!!data ? data : undefined);

				if (this.isValid(resolved)) {
					return resolved.value;
				} else {
					return;
				}
			} catch (e) {
				this._storage.removeItem(key);
				throw e;
			}
		}
	}

	/**
	 * Removes an object from the storage
	 * @param key
	 */
	public remove = (key: string) => {
		this._storage.removeItem(key);
	}

	/**
	 * Clears the storage
	 */
	public clear = () => {
		this._storage.clear();
	}

	/**
	 * Checks whether the extracted element from the response is still valid
	 * @param element
	 */
	private isValid = ({ timestamp, expiration }: IStorageItem = {} as IStorageItem): boolean => {
		if (timestamp && expiration) {
			return expiration * 60 * 60 * 1000 > Date.now() - timestamp;
		} else {
			return false;
		}
	}

	/**
	 * Sets a mock storage for non-browser environments
	 */
	private setMockStorage = (): Storage => {
		let store: any = {};

		const getItem = (key: string) => store[key] ? store[key] : undefined;
		const setItem = (key: string, data: any) => store[key] = data;
		const removeItem = (key: string) => delete store[key];
		const clear = () => store = {};

		return {
			clear,
			getItem,
			removeItem,
			setItem
		} as any;
	}
}

export default new StorageService();
